import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { studentService, parentService } from '../../services/api';
import dayjs from 'dayjs';
import DatePickerField from '../../components/DatePickerField';

const schema = yup.object({
  first_name: yup.string().required('Nombre es requerido'),
  last_name: yup.string().required('Apellido es requerido'),
  sponsor: yup.string().nullable(),
  grade: yup.number()
    .required('Grado es requerido')
    .min(0, 'Grado debe ser entre 0 y 12')
    .max(12, 'Grado debe ser entre 0 y 12')
    .typeError('Debe ser un número válido'),
  group: yup.string().nullable(), // Nuevo campo para el grupo
  parent_id: yup.number().nullable(),
  status: yup.string().required('Estado es requerido'),
  enrollment_date: yup.string().nullable(),
  notes: yup.string().nullable()
});

const grades = [
  { value: 0, label: 'Preescolar' },
  { value: 1, label: '1er Grado' },
  { value: 2, label: '2do Grado' },
  { value: 3, label: '3er Grado' },
  { value: 4, label: '4to Grado' },
  { value: 5, label: '5to Grado' },
  { value: 6, label: '6to Grado' },
  { value: 7, label: '7mo Grado' },
  { value: 8, label: '8vo Grado' },
  { value: 9, label: '9no Grado' },
  { value: 10, label: '10mo Grado' },
  { value: 11, label: '11vo Grado' },
  { value: 12, label: '12vo Grado' }
];

// Grupos (opcionales)
const groups = [
  { value: '', label: 'Sin grupo' },
  { value: 'A', label: 'Grupo A' },
  { value: 'B', label: 'Grupo B' },
  { value: 'C', label: 'Grupo C' },
  { value: 'Prescolar', label: 'Preescolar' },
  { value: '6&7', label: '6to y 7mo Combinado' },
  { value: '8&9', label: '8vo y 9no Combinado' }
];

const statuses = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'baja', label: 'Baja' },
  { value: 'finalizado', label: 'Finalizado' },
  { value: 'exento', label: 'Exento (Sin pago)' } 
];

// Definir qué campos pertenecen a cada paso
const stepFields = {
  0: ['first_name', 'last_name', 'enrollment_date', 'sponsor'],
  1: ['grade', 'status', 'notes'],
  2: ['parent_id']
};

const StudentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parents, setParents] = useState<any[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [showParentForm, setShowParentForm] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    trigger,
    getValues
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      sponsor: '',
      grade: 0,
      group: '',
      parent_id: null,
      status: 'active',
      enrollment_date: dayjs().format('YYYY-MM-DD'),
      notes: ''
    }
  });

  useEffect(() => {
    loadParents();
    
    if (isEdit) {
      loadStudent();
    }
  }, [id]);

  const loadStudent = async () => {
    try {
      setLoading(true);
      const response = await studentService.getById(parseInt(id!));
      const studentData = response.data;

          // 👈 FORMATEAR LA FECHA
    const formattedData = {
      ...studentData,
      enrollment_date: studentData.enrollment_date 
        ? dayjs(studentData.enrollment_date).format('YYYY-MM-DD')
        : null
    };

      reset(formattedData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar estudiante');
    } finally {
      setLoading(false);
    }
  };

  const loadParents = async () => {
    try {
      const response = await parentService.getAll({ per_page: 400, active: true });
      setParents(response.data.data);
    } catch (err) {
      console.error('Error loading parents:', err);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      if (isEdit) {
        await studentService.update(parseInt(id!), data);
      } else {
        await studentService.create(data);
      }

      navigate('/students');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar estudiante');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Información Personal', 'Datos Académicos', 'Información del Tutor'];

  // Validar solo los campos del paso actual
  const validateCurrentStep = async () => {
    const fieldsToValidate = stepFields[activeStep as keyof typeof stepFields];
    const result = await trigger(fieldsToValidate as any);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCreateParent = async (parentData: any) => {
    try {
      const response = await parentService.create(parentData);
      setParents([...parents, response.data.parent]);
      setValue('parent_id', response.data.parent.id);
      setShowParentForm(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear padre');
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/students" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Estudiante' : 'Nuevo Estudiante'}
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form>
        <Grid container spacing={3}>
          {/* Step 1: Personal Information */}
          {activeStep === 0 && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="first_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Nombre"
                      error={!!errors.first_name}
                      helperText={errors.first_name?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="last_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Apellidos"
                      error={!!errors.last_name}
                      helperText={errors.last_name?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="enrollment_date"
                  control={control}
                  render={({ field }) => (
                    <DatePickerField
                      {...field}
                      label="Fecha de Ingreso"
                      error={!!errors.enrollment_date}
                      helperText={errors.enrollment_date?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="sponsor"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Patrocinador"
                      error={!!errors.sponsor}
                      helperText={errors.sponsor?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          {/* Step 2: Academic Information */}
{activeStep === 1 && (
  <>
    {/* Campo de Grado (numérico) */}
    <Grid size={{ xs: 12, sm: 6 }}>
      <Controller
        name="grade"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            label="Grado *"
            error={!!errors.grade}
            helperText={errors.grade?.message}
            disabled={loading}
          >
            {grades.map((grade) => (
              <MenuItem key={grade.value} value={grade.value}>
                {grade.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid>

    {/* Campo de Grupo (opcional) */}
    <Grid size={{ xs: 12, sm: 6 }}>
      <Controller
        name="group"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            label="Grupo (opcional)"
            error={!!errors.group}
            helperText={errors.group?.message}
            disabled={loading}
          >
            {groups.map((group) => (
              <MenuItem key={group.value} value={group.value}>
                {group.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid>

    {/* Campo de Estado */}
    <Grid size={{ xs: 12, sm: 6 }}>
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            fullWidth
            label="Estado"
            error={!!errors.status}
            helperText={errors.status?.message}
            disabled={loading}
          >
            {statuses.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid>

    {/* Campo de Notas */}
    <Grid size={{ xs: 12 }}>
      <Controller
        name="notes"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Notas"
            multiline
            rows={4}
            error={!!errors.notes}
            helperText={errors.notes?.message}
            disabled={loading}
          />
        )}
      />
    </Grid>
  </>
)}

          {/* Step 2: Academic Information */}
          {/*activeStep === 1 && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="grade"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Grado"
                      error={!!errors.grade}
                      helperText={errors.grade?.message}
                      disabled={loading}
                    >
                      {grades.map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          {grade}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Estado"
                      error={!!errors.status}
                      helperText={errors.status?.message}
                      disabled={loading}
                    >
                      {statuses.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Notas"
                      multiline
                      rows={4}
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                      disabled={loading}
                    />
                  )}
                />
              </Grid>
            </>
          )*/}

          {/* Step 3: Parent Information */}
          {activeStep === 2 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">
                        Información del Padre/Tutor
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setShowParentForm(!showParentForm)}
                        variant="outlined"
                        size="small"
                      >
                        {showParentForm ? 'Cancelar' : 'Nuevo Padre'}
                      </Button>
                    </Box>

                    {showParentForm ? (
                      <ParentForm onCreate={handleCreateParent} />
                    ) : (
                      <Controller
                        name="parent_id"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="Seleccionar Padre/Tutor"
                            error={!!errors.parent_id}
                            helperText={errors.parent_id?.message}
                            disabled={loading}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>Sin asignar</em>
                            </MenuItem>
                            {parents.map((parent) => (
                              <MenuItem key={parent.id} value={parent.id}>
                                {parent.spanish_name || parent.russian_name}
                                {parent.email && ` (${parent.email})`}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Navigation Buttons */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="space-between">
              <Button
                type="button"
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
                variant="outlined"
              >
                Anterior
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  onClick={handleSubmit(onSubmit)}
                >
                  {loading ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  type="button"
                >
                  Siguiente
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

// Componente para formulario rápido de padre
const ParentForm: React.FC<{ onCreate: (data: any) => void }> = ({ onCreate }) => {
  const [parentData, setParentData] = useState({
    russian_name: '',
    spanish_name: '',
    email: '',
    phone: '',
    country_code: '+7',
    notes: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate(parentData);
  };

  return (
    <Box 
      component="form"
      onSubmit={handleSubmit}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Nombre en Ruso"
            value={parentData.russian_name}
            onChange={(e) => setParentData({...parentData, russian_name: e.target.value})}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Nombre en Español"
            value={parentData.spanish_name}
            onChange={(e) => setParentData({...parentData, spanish_name: e.target.value})}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={parentData.email}
            onChange={(e) => setParentData({...parentData, email: e.target.value})}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Teléfono"
            value={parentData.phone}
            onChange={(e) => setParentData({...parentData, phone: e.target.value})}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            fullWidth
            label="Código País"
            value={parentData.country_code}
            onChange={(e) => setParentData({...parentData, country_code: e.target.value})}
          >
            <MenuItem value="+7">🇷🇺 Rusia (+7)</MenuItem>
            <MenuItem value="+53">🇨🇺 Cuba (+53)</MenuItem>
            <MenuItem value="+1">🇺🇸 USA/Canadá (+1)</MenuItem>
            <MenuItem value="+34">🇪🇸 España (+34)</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Notas"
            multiline
            rows={2}
            value={parentData.notes}
            onChange={(e) => setParentData({...parentData, notes: e.target.value})}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button type="submit" variant="contained">
            Crear Padre
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentForm;